import { useEffect, useRef, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Link, useTypedParams } from '@sorare/routing';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { GemIcon } from '@sorare/core/src/atoms/icons/GemIcon';
import { Plus } from '@sorare/core/src/atoms/icons/Plus';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { UserNav } from '@sorare/core/src/components/navigation/UserNav';
import { ANY_SPORT_MARKET } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { getRewardImageUrl } from '@sorare/core/src/lib/rewards';

import { HeaderBuyGemsDialog } from './HeaderBuyGemsDialog';

const Root = styled(Vertical).attrs({ gap: 0 })<{ $scrollPercentage: number }>`
  isolation: isolate;
  &:before {
    z-index: -1;
    content: '';
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background: rgba(var(--c-rgb-black), 0.4);
    backdrop-filter: blur(8px);
    opacity: ${({ $scrollPercentage }) => $scrollPercentage};
  }
`;

const FirstRow = styled(SBHorizontal)`
  padding: var(--unit) var(--double-unit);
`;

const BalanceButton = styled(ButtonBase)`
  border-radius: var(--unit);
  padding: var(--half-unit) var(--intermediate-unit);
  background: var(--c-nd-50);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--half-unit);
  &:hover,
  &:focus {
    background: var(--c-nd-200);
  }
  & svg {
    width: var(--unit);
    height: var(--unit);
  }
`;

const PlusIcon = styled(Horizontal).attrs({ gap: 0, center: true })`
  background-color: var(--c-blue-400);
  border-radius: 50%;
  width: var(--intermediate-unit);
  height: var(--intermediate-unit);
  outline: 1px solid var(--c-nd-50-opaque);
  margin-left: -7px;
`;

export const TransparentMobileWebAppHeader = () => {
  const { sport, generateSportContextPath } = useSportContext();
  const { currentUser } = useCurrentUserContext();
  const ref = useRef<HTMLDivElement>(null);
  const [scrollPercentage, setScrollPercentage] = useState(0);
  const [openBuyGemsDialog, setOpenBuyGemsDialog] = useState(false);
  const { type, rarity } =
    //supposing we are on a rarity-specific page
    useTypedParams<{ type?: Rarity; rarity?: Rarity }>();

  const currentRarity = rarity || type;

  const shardsBalance = currentUser?.shardsChests.find(
    ({ rarity: chestRarity }) => chestRarity === currentRarity
  )?.cardShardsCount;

  useEffect(() => {
    const handleScroll = () => {
      setScrollPercentage(
        Math.min(
          1,
          Number((window.scrollY / (ref.current?.clientHeight || 1)).toFixed(2))
        )
      );
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return (
    <Root ref={ref} $scrollPercentage={scrollPercentage}>
      <FirstRow>
        <Horizontal>
          <UserNav sport={sport} size="small" />
          <BalanceButton onClick={() => setOpenBuyGemsDialog(true)}>
            <LabelM bold>
              <FormattedNumber
                value={currentUser?.gemsBalances[0]?.amount || 0}
              />
            </LabelM>
            <GemIcon size={1.5} />
            <PlusIcon>
              <Plus fill="white" />
            </PlusIcon>
          </BalanceButton>
          {!!currentRarity && (
            <BalanceButton
              as={Link}
              to={`${generateSportContextPath(
                ANY_SPORT_MARKET
              )}?rarity=${currentRarity}`}
            >
              <LabelM bold>
                <FormattedNumber value={shardsBalance || 0} />
              </LabelM>
              <img
                src={proxyUrl(getRewardImageUrl('shard', currentRarity), {
                  cropWidth: 80,
                })}
                alt=""
                width={16}
                height={16}
              />
            </BalanceButton>
          )}
        </Horizontal>
      </FirstRow>
      <div id="page-header-second-row" />
      <HeaderBuyGemsDialog
        open={openBuyGemsDialog}
        onClose={() => setOpenBuyGemsDialog(false)}
      />
    </Root>
  );
};
